// @ts-nocheck
import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, ChevronUp, Filter, Shield, SlidersHorizontal, Trophy, X } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle, 
  SheetTrigger,
  SheetClose
} from "@/components/ui/sheet";
import { ISearchPagination } from "@/integration/application/interfaces/searchPagination.interface.ts";
import useGamePublic from "@integration/game/hooks/useGamePublic.ts";
import { useQuery } from "@tanstack/react-query";
import {
  optionsFetchSearchMyTeams,
  optionsFetchSearchTeamByNameAndTag,
} from "@/config/plugins/queryOptions.reactquery";
import ModalCreateTeam from "@/components/Modal/CreateTeam";
import TeamCard from "@/pages/team/components/TeamCard.tsx";
import TeamCardSkeleton from "@/pages/team/components/TeamCardSkeleton.tsx";
import useUser from "@integration/user/hooks/useUser.ts";

const MyTeams = () => {
  const { userAuth } = useUser();

  //Integração com o backend
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const { gamesFilter } = useGamePublic();
  const [selectedGame, setSelectedGame] = useState<string | null>("");
  const [isPageChanging, setIsPageChanging] = useState(false);

  const searchTeamsParams: ISearchPagination = {
    search: searchTerm,
    page: currentPage,
    limit: 20,
    order: "asc",
    entityId: userAuth?.id,
  };

  const searchMyTeamsParams: ISearchPagination = {
    search: searchTerm,
    page: currentPage,
    limit: 20,
    order: "asc",
    gameId: selectedGame,
    entityId: userAuth?.id,
  };

  // Use useQuery directly to get loading states for teams
  const { data: teams } = useQuery(
    optionsFetchSearchTeamByNameAndTag(searchTeamsParams)
  );

  const { data: dataMyTeams, isLoading: isMyTeamsLoading } = useQuery(
    optionsFetchSearchMyTeams(searchMyTeamsParams)
  );

  const handlePageChange = (page: number) => {
    setIsPageChanging(true);
    setCurrentPage(page);
    // Reset isPageChanging after a short delay to allow for animation
    setTimeout(() => {
      setIsPageChanging(false);
    }, 300);
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  const handleGameSelect = (gameId: string) => {
    setSelectedGame(selectedGame === gameId ? null : gameId);
  };

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);

  // Use API data for teams
  const filteredTeams = useMemo(() => {
    if (!teams?.data) return [];
    return teams.data;
  }, [teams]);

  const filteredMyTeams = useMemo(() => {
    if (!dataMyTeams?.data) return [];
    return dataMyTeams.data;
  }, [dataMyTeams]);

  return (
    <div className="min-h-screen md:ml-10 mb-10">
      <div className="mx-auto px-4 py-8">
        {/* Header section */}
        <div className="mb-8 bg-gradient-to-br from-gray-800/80 to-gray-900/80 p-8 rounded-xl border border-red-500/20 shadow-2xl backdrop-blur-sm relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-red-600/10 via-red-700/5 to-transparent"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/5 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-red-600/5 rounded-full filter blur-3xl translate-y-1/2 -translate-x-1/2"></div>

          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-red-500 to-red-700 p-3 rounded-xl shadow-lg">
                  <Trophy size={28} className="text-white" />
                </div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                  Gerenciamento de Times
                </h1>
              </div>
            </div>

            <div className="h-0.5 w-32 bg-gradient-to-r from-red-500 via-red-600 to-transparent mb-6"></div>

            <p className="text-gray-300 text-lg max-w-3xl leading-relaxed">
              Conecte-se, forme equipes e gerene sua própria história de
              e-sports.
            </p>
          </div>
        </div>
      </div>

      <div className="w-full mx-auto px-4">
        {/* Desktop Filters */}
        <div className="hidden sm:block mb-6 bg-gradient-to-b from-gray-800/60 to-gray-900/60 p-6 rounded-xl shadow-xl border border-gray-700/50">
          <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
            <div className="flex items-center justify-between gap-3 mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-red-500/20 to-red-700/20 p-2.5 rounded-xl shadow-md border border-red-500/10">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-red-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                  Filtros
                </h2>
              </div>
              <CollapsibleTrigger asChild>
                <button className="p-2 rounded-lg bg-gray-800/80 hover:bg-gray-700/80 border border-gray-700">
                  {filtersOpen ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </CollapsibleTrigger>
            </div>

            <CollapsibleContent className="transition-all duration-300">
              {/* Type filter buttons */}
              <div className="mb-6">
                <div className="mt-8 mb-8">
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                      <div className="bg-gradient-to-br from-purple-500/20 to-blue-600/20 p-1.5 rounded-lg shadow-sm border border-purple-500/10">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-purple-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                          />
                        </svg>
                      </div>
                      <h3 className="text-xl font-semibold bg-gradient-to-r from-purple-300 to-blue-300 bg-clip-text text-transparent">
                        Filtrar por Jogo
                      </h3>
                    </div>

                    <div className="bg-gray-800/30 p-4 rounded-xl border border-gray-700/50">
                      <div className="flex flex-wrap gap-3">
                        <motion.button
                          onClick={() => setSelectedGame(null)}
                          className={`px-4 py-2 rounded-xl ${
                            selectedGame === null
                              ? "bg-gradient-to-r from-red-600 to-red-700 text-white shadow-md"
                              : "bg-gray-800/80 text-gray-300 border border-gray-700"
                          }`}
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          Todos
                        </motion.button>
                        {gamesFilter.map((game) => (
                          <motion.button
                            key={game.id}
                            onClick={() => handleGameSelect(game.id)}
                            className={`px-4 py-2 rounded-xl transition-all duration-300 flex items-center gap-2 ${
                              selectedGame === game.id
                                ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-md"
                                : "bg-gray-800/80 text-gray-300 hover:bg-gray-700/80 border border-gray-700 hover:border-purple-500/30"
                            }`}
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                          >
                            {game.icon && (
                              <img
                                src={game.icon}
                                alt={game.name}
                                className="w-5 h-5 rounded-md"
                              />
                            )}
                            {game.name}
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search input */}
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="bg-gradient-to-br from-red-500/20 to-red-600/20 p-1.5 rounded-lg shadow-sm border border-red-500/10">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-red-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                    <label
                      htmlFor="search"
                      className="text-lg font-semibold bg-gradient-to-r from-red-300 to-red-400 bg-clip-text text-transparent"
                    >
                      Buscar Times
                    </label>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      id="search"
                      placeholder={`Digite o nome do `}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-3.5 bg-gray-800/50 border border-gray-700 rounded-xl text-white pl-12"
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 absolute left-4 top-4 text-red-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>

        {/* Mobile Filter Button */}
        <div className="sm:hidden mb-6 flex justify-between items-center">
          <motion.div variants={itemVariants} className="flex-1">
            <Sheet open={isFilterSheetOpen} onOpenChange={setIsFilterSheetOpen}>
              <SheetTrigger asChild>
                <motion.button
                  className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-red-600/20 to-red-700/20 hover:from-red-600/30 hover:to-red-700/30 border border-red-500/30 hover:border-red-500/50 rounded-xl text-white transition-all duration-300 shadow-md w-full"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SlidersHorizontal className="h-5 w-5 text-red-400" />
                  <span className="font-medium">Filtrar times</span>
                  <div className="ml-2 flex items-center justify-center w-6 h-6 bg-red-500/20 rounded-full border border-red-500/30">
                    <span className="text-xs font-bold text-red-400">
                      {selectedGame ? "1" : "0"}
                    </span>
                  </div>
                </motion.button>
              </SheetTrigger>

              <SheetContent side="bottom" className="bg-gradient-to-b from-gray-800/95 to-gray-900/95 backdrop-blur-md border-t border-gray-700/50 text-white p-6 rounded-t-2xl">
                <SheetHeader className="mb-6">
                  <div className="flex items-center justify-between">
                    <SheetTitle className="text-xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent flex items-center gap-2">
                      <Filter className="h-5 w-5 text-red-400" />
                      Filtrar times
                    </SheetTitle>

                  </div>
                  <div className="h-0.5 w-24 bg-gradient-to-r from-red-500/30 to-transparent mt-2"></div>
                </SheetHeader>

                {/* Search bar */}
                <div className="mb-6">

                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Digite o nome do time..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 hover:border-red-500/30 focus:border-red-500/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500/50 pl-12 transition-all duration-300 shadow-inner"
                    />
                    <Filter className="h-5 w-5 absolute left-4 top-3.5 text-red-400" />
                  </div>
                </div>

                {/* Game filter */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Filter className="h-4 w-4 text-red-400" />
                    <span className="text-sm font-medium text-gray-300">Filtrar por Jogo</span>
                  </div>
                  <div className="bg-gray-800/30 p-4 rounded-xl border border-gray-700/50">
                    <div className="flex flex-wrap gap-3">
                      <motion.button
                        onClick={() => setSelectedGame(null)}
                        className={`px-4 py-2 rounded-xl transition-all duration-300 ${
                          selectedGame === null
                            ? "bg-gradient-to-r from-red-600 to-red-700 text-white shadow-md"
                            : "bg-gray-800/80 text-gray-300 hover:bg-gray-700/80 border border-gray-700 hover:border-red-500/30"
                        }`}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                      >
                        Todos
                      </motion.button>
                      {gamesFilter.map((game) => (
                        <motion.button
                          key={game.id}
                          onClick={() => handleGameSelect(game.id)}
                          className={`px-4 py-2 rounded-xl transition-all duration-300 flex items-center gap-2 ${
                            selectedGame === game.id
                              ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-md"
                              : "bg-gray-800/80 text-gray-300 hover:bg-gray-700/80 border border-gray-700 hover:border-purple-500/30"
                          }`}
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                        >
                          {game.icon && (
                            <img
                              src={game.icon}
                              alt={game.name}
                              className="w-5 h-5 rounded-md"
                            />
                          )}
                          {game.name}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Apply and Clear buttons */}
                <div className="flex gap-3 mt-8">
                  <SheetClose asChild>
                    <motion.button
                      onClick={() => {
                        setSelectedGame(null);
                        setSearchTerm("");
                      }}
                      className="flex-1 px-4 py-3 bg-gray-800/80 border border-gray-700/50 hover:border-red-500/30 rounded-xl text-gray-300 font-medium transition-all duration-300"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Limpar filtros
                    </motion.button>
                  </SheetClose>
                  <SheetClose asChild>
                    <motion.button
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 rounded-xl text-white font-medium transition-all duration-300 shadow-md"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Aplicar filtros
                    </motion.button>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </motion.div>
        </div>

        {/* Results heading */}
        <div className="mb-8 bg-gradient-to-b from-gray-800/40 to-gray-900/40 p-6 rounded-xl border border-gray-700/30 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <div
              className={`p-2.5 rounded-xl shadow-md border  bg-green-500/20 border-green-500/10`}
            >
              <Shield className="h-6 w-6 text-green-400" />
            </div>

            <h2 className="text-2xl font-bold bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-green-300">
              Times
            </h2>
            <ModalCreateTeam />
          </div>
          <div className="h-0.5 w-24 mt-3 mb-1 bg-gradient-to-r from-gray-500/30 to-transparent"></div>
        </div>

        <motion.div variants={itemVariants} className="relative">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {isMyTeamsLoading || isMyTeamsLoading || isPageChanging ? (
              // Show skeletons when loading
              Array.from({ length: 8 }).map((_, index) => (
                <TeamCardSkeleton key={index} />
              ))
            ) : filteredMyTeams?.length > 0 ? (
              // Show actual teams when loaded
              filteredMyTeams.map((team, index) => (
                <motion.div
                  key={team.entityId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <TeamCard team={team} gamesFilter={gamesFilter} />
                </motion.div>
              ))
            ) : (
              <div className="col-span-3 text-center py-16 bg-gray-800/30 rounded-xl border border-gray-700/50">
                <p className="text-gray-400 text-lg">Nenhum time encontrado</p>
              </div>
            )}
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default MyTeams;
